import { useEffect, useMemo } from 'react';

import { useGetSettlementsQuery } from '@app/store/slices/settlements/settlementsApi';
import { usePageTimer } from '@app/shared/hooks/usePageTimer';
import { usePurchaseSettlement } from '@app/shared/hooks/usePurchaseSettlement';
import { Settlement as SettlementType } from '@app/store/slices/settlements/settlementsTypes';

export function useSettlementGate(
  settlementIndex: number,
  timerKey: string,
  timerDuration: number
) {
  const { purchaseSettlement, isPurchasingSettlement } = usePurchaseSettlement();
  const { trigger, isExpired, lastAt } = usePageTimer(timerKey, timerDuration);

  const { data: settlements, isLoading: isSettlementsLoading } = useGetSettlementsQuery();

  const requiredSettlement: SettlementType | undefined = useMemo(
    () => settlements?.[settlementIndex],
    [settlements, settlementIndex]
  );

  const isRequiredSettlementBought = !!requiredSettlement?.isPurchased;

  useEffect(() => {
    if (!isRequiredSettlementBought && lastAt === null) {
      trigger();
    }
  }, [isRequiredSettlementBought, lastAt, trigger]);

  const handlePurchase = async () => {
    await purchaseSettlement(requiredSettlement);
  };

  return {
    settlements,
    requiredSettlement,
    isRequiredSettlementBought,
    isSettlementsLoading,
    isOpen: !isRequiredSettlementBought && isExpired,
    handlePurchase,
    isPurchasingSettlement,
  };
}
